import {
  forwardRef,
  useMemo,
  useState,
  useCallback,
  useRef,
  useImperativeHandle
} from 'react'

import { tv } from 'tailwind-variants'

import { cn } from '@/utils'

export const textAreaVariants = tv({
  slots: {
    containerSlot: 'relative',
    textareaSlot:
      'text-surface-white-400 placeholder:text-surface-white-400 border-surface-white-300 focus:border-surface-white-400 focus:ring-surface-white-400 focus:text-surface-black-50 bg-surface-white-50 font-body field-sizing-content w-full resize-y rounded-sm border px-4 py-3 font-normal outline-none ring-0'
  },
  variants: {
    size: {
      sm: {
        textareaSlot: 'text-caption-sm min-h-20 py-2'
      },
      md: {
        textareaSlot: 'text-body-md min-h-24 py-3'
      },
      lg: {
        textareaSlot: 'text-body-lg min-h-32 py-4'
      }
    },
    error: {
      true: {
        textareaSlot:
          'border-danger-100 text-danger-200 placeholder:text-danger-100 focus:border-danger-200 focus:ring-danger-100 font-semibold focus:ring-1'
      }
    },
    disabled: {
      true: {
        textareaSlot:
          'bg-surface-white-200 text-surface-white-400 border-surface-white-300 cursor-not-allowed'
      }
    },
    readOnly: {
      true: {
        textareaSlot:
          'bg-surface-white-300 text-surface-white-400 cursor-default font-bold'
      }
    },
    expandable: {
      true: {
        textareaSlot: 'min-h-auto resize-none'
      }
    }
  },
  defaultVariants: {
    size: 'md'
  }
})

export type TTextareaProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'size' | 'className'
> & {
  error?: string
  placeholder?: string
  className?: string
  size?: 'sm' | 'md' | 'lg'
  expandable?: boolean
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  onEnter?: () => void
  helperTextId?: string
  errorTextId?: string
  maxWidth?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TTextareaProps>(
  (
    {
      placeholder,
      size,
      maxWidth,
      error,
      disabled,
      readOnly,
      required,
      expandable,
      onEnter,
      helperTextId,
      errorTextId,
      ...props
    },
    ref
  ) => {
    const {
      value: propValue,
      defaultValue: propDefaultValue,
      onChange: propOnChange,
      name,
      id,
      ...restProps
    } = props

    const textareaRef = useRef<HTMLTextAreaElement>(null)
    useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement)

    const describedBy = useMemo(() => {
      const ids = [helperTextId, errorTextId].filter(Boolean)
      return ids.length ? ids.join(' ') : undefined
    }, [helperTextId, errorTextId])

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (props.onKeyDown) props.onKeyDown(e)
        if (e.key === 'Enter' && e.ctrlKey && onEnter) {
          e.preventDefault()
          onEnter()
        }
      },
      [props, onEnter]
    )

    const isControlled = propValue !== undefined
    const [uncontrolledValue, setUncontrolledValue] = useState<string>(() => {
      if (propDefaultValue === undefined || propDefaultValue === null) return ''
      if (Array.isArray(propDefaultValue as unknown))
        return (propDefaultValue as unknown[]).join(',')
      return String(propDefaultValue as unknown)
    })

    const currentValue = isControlled
      ? String((propValue as unknown) ?? '')
      : uncontrolledValue

    const { containerSlot, textareaSlot } = textAreaVariants({
      size,
      error: !!error,
      disabled: !!disabled,
      readOnly: !!readOnly,
      expandable: !!expandable
    })

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (!isControlled) setUncontrolledValue(e.target.value)
        if (propOnChange) propOnChange(e)
      },
      [isControlled, propOnChange]
    )

    return (
      <div className={containerSlot()}>
        <textarea
          className={textareaSlot({ className: cn(`max-w-[${maxWidth}]`) })}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          ref={textareaRef}
          onKeyDown={handleKeyDown}
          value={currentValue}
          onChange={handleChange}
          name={name}
          id={id || name}
          {...restProps}
        />
      </div>
    )
  }
)

Textarea.displayName = 'Textarea'
